import { useState } from "react";
import * as Yup from "yup";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { PriceInput } from "components/PriceInput/PriceInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useModal } from "hooks/useModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import {
  useGetSITPropertyDataQuery,
  useAddSITPropertyDataMutation,
  useDeleteSITPropertyDataMutation,
} from "store/apis/stateIncomeTax";
import {
  SITPropertyItem,
  SITPropertySortBy,
  SITSortOrder,
} from "types/Models/stateIncomeTax";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { formatAmount } from "utils/foramtAmount";
import Wind from "components/icons/Wind";
import {
  OnChangeFn,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { DeleteIcon } from "components/icons/delete";
import { useCurrentEntityStates } from "components/Entity/EntityFormEngine/AddJurisdiction";

const SORT_ID_MAP: Record<string, SITPropertySortBy> = {
  state_name: "state",
  owned_property: "owned_property",
  rented_property: "rented_property",
};

const columnHelper = createColumnHelper<SITPropertyItem>();

type Props = {
  season: number;
};

type AddPropertyFormValues = {
  state_uuid: string;
  owned_property: string;
  rented_property: string;
};

const validationSchema = Yup.object({
  state_uuid: Yup.string().required("State is required"),
  owned_property: Yup.string().required("Owned property value is required"),
  rented_property: Yup.string().required("Rented property value is required"),
});

const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-4 t-py-16">
    <Wind className="t-text-text-30" />
    <div className="t-flex t-flex-col t-items-center t-gap-1">
      <span className="t-text-body-sm t-text-text-100">
        No property data added
      </span>
      <span className="t-text-body-sm t-text-text-30">
        Add property values per state to include them in the apportionment
        calculation.
      </span>
    </div>
    <Button customType="primary" onClick={onAdd}>
      Add Property
    </Button>
  </div>
);

const AddPropertyModal = ({
  isOpen,
  close,
  season,
  groupId,
  entityId,
}: {
  isOpen: boolean;
  close: () => void;
  season: number;
  groupId: string;
  entityId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const [addProperty] = useAddSITPropertyDataMutation();
  const { data: states = [], isLoading: isStatesLoading } =
    useCurrentEntityStates(entityId);

  const stateOptions = states.map((s) => ({
    value: s.state_id,
    label: s.name,
  }));

  const handleSubmit = async (values: AddPropertyFormValues) => {
    try {
      await addProperty({
        groupId,
        entityId,
        season,
        state_uuid: values.state_uuid,
        owned_property: values.owned_property,
        rented_property: values.rented_property,
      }).unwrap();
      successToast({ message: "Property data saved." });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Add Property</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <FormikForm
          initialValues={{
            state_uuid: "",
            owned_property: "",
            rented_property: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <>
              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <Combobox
                  label="State"
                  menuPortalTarget={document.body}
                  isLoading={isStatesLoading}
                  options={stateOptions}
                  value={
                    stateOptions.find((o) => o.value === values.state_uuid) ??
                    null
                  }
                  onChange={(option) => {
                    if (option && "value" in option) {
                      setFieldValue("state_uuid", option.value);
                    }
                  }}
                  placeholder="Select state"
                />
                <PriceInput
                  label="Owned Property"
                  name="owned_property"
                  placeholder="0.00"
                />
                <PriceInput
                  label="Rented Property"
                  name="rented_property"
                  placeholder="0.00"
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button" onClick={close} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

const DeleteConfirmModal = ({
  isOpen,
  close,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  close: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => (
  <Modal.Root open={isOpen} onOpenChange={close}>
    <Modal.Content size="small">
      <Modal.Header>
        <Modal.Title>Remove Property Data</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <div className="t-text-body-sm">
          Are you sure you want to remove this property entry? This action
          cannot be undone.
        </div>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button onClick={close} disabled={isLoading}>
            Cancel
          </Button>
        </Modal.RawClose>
        <Button
          customType="danger"
          onClick={onConfirm}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Remove
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

const PropertyTable = ({
  rows,
  sorting,
  onSortingChange,
  onDelete,
}: {
  rows: SITPropertyItem[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onDelete: (stateUuid: string) => void;
}) => {
  const columns = [
    columnHelper.accessor("state_code", {
      id: "state_code",
      size: 80,
      enableSorting: false,
      header: () => <HeadCellContent title="Code" />,
      cell: (info) => (
        <span className="t-text-body-sm t-font-semibold t-text-text-60">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("state_name", {
      id: "state_name",
      header: (header) => (
        <HeadCellContent
          title="State"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("owned_property", {
      id: "owned_property",
      size: 180,
      header: (header) => (
        <HeadCellContent
          title="Owned Property"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
          {formatAmount(Number(info.getValue()))}
        </span>
      ),
    }),
    columnHelper.accessor("rented_property", {
      id: "rented_property",
      size: 180,
      header: (header) => (
        <HeadCellContent
          title="Rented Property"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => (
        <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
          {formatAmount(Number(info.getValue()))}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row, {
      id: "actions",
      size: 60,
      enableSorting: false,
      header: () => null,
      cell: (info) => (
        <div className="t-flex t-justify-end">
          <Button
            size="small"
            customType="ghost_icon"
            type="button"
            onClick={() => onDelete(info.getValue().state_uuid)}
          >
            <span className="t-text-text-30">
              <DeleteIcon color="currentColor" size="16" />
            </span>
          </Button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  });

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{ width: header.getSize() }}
                  isUpperCase={false}
                  className="t-group"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

export const SITPropertyData = ({ season }: Props) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const addModal = useModal();
  const [stateToDelete, setStateToDelete] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [deleteProperty, { isLoading: isDeleting }] =
    useDeleteSITPropertyDataMutation();

  const sort_by = sorting[0] ? SORT_ID_MAP[sorting[0].id] : undefined;
  const sort_order: SITSortOrder | undefined = sorting[0]
    ? sorting[0].desc
      ? "desc"
      : "asc"
    : undefined;

  const {
    data: response,
    isLoading,
    isSuccess,
  } = useGetSITPropertyDataQuery(
    { groupId, entityId, season, sort_by, sort_order },
    { skip: !groupId || !entityId }
  );

  const rows = response?.data ?? [];
  const isEmpty = rows.length === 0;

  const handleDelete = async () => {
    if (!stateToDelete) return;
    try {
      await deleteProperty({
        groupId,
        entityId,
        season,
        state_uuid: stateToDelete,
      }).unwrap();
      successToast({ message: "Property data removed." });
      setStateToDelete(null);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={isEmpty}>
        <Async.Empty>
          <EmptyState onAdd={addModal.open} />
        </Async.Empty>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-flex t-justify-end">
              <Button customType="primary" size="small" onClick={addModal.open}>
                Add Property
              </Button>
            </div>
            <PropertyTable
              rows={rows}
              sorting={sorting}
              onSortingChange={setSorting}
              onDelete={setStateToDelete}
            />
          </div>
        </Async.Success>
      </Async.Root>

      <AddPropertyModal
        isOpen={addModal.isOpen}
        close={addModal.close}
        season={season}
        groupId={groupId}
        entityId={entityId}
      />

      <DeleteConfirmModal
        isOpen={Boolean(stateToDelete)}
        close={() => setStateToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </>
  );
};
